import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { selectAppStateSelectedCustomer } from '@app/ngrx-customer/store';
import { State } from '@app/store';
import { InventoryService } from '@inventory/services/inventory.service';
import { Store } from '@ngrx/store';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { isDateGreater } from '@shared/helpers/calendar.helpers';
import {
  FOOD_COST_ACTIONS,
  foodCostReportAdapter,
  FoodCostReportState,
  FoodCostSortEnum,
  FoodCostTarget,
  INVENTORY_ACTIONS,
  selectFoodCostSortOptions,
  selectFoodCostState,
  SortDirections,
  InventoryAnalyticsConstants,
  ANALYTICS_ACTIONS,
  pageLoadedTransformer,
  modalAnalyticsTransformer,
} from '@usf/ngrx-inventory';
import { Preferences } from '@usf/user-types/user-preference';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  first,
  firstValueFrom,
  map,
  Observable,
  switchMap,
  take,
} from 'rxjs';
import { UserPreferencesState } from '../../../../user/models/user-preferences-state';
import { UserService } from '../../../../user/services';
import { UserActions } from '../../../../user/store/actions/action-types';
import {
  FoodCostOverviewData,
  FoodCostRangeLoadingState,
  FoodCostTotalData,
  FoodCostViewModel,
} from '../models/food-cost-view-model.model';
import { transformFoodCostReport } from '../transformers/food-cost-calculator-transformer';
import { selectUser } from '@app/user/store';
import { EllipsisOptionsPopoverComponent } from '../../../../shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { CostReportCardEllipsisClick } from '@inventory/models/cost-report-model.model';
import { TranslateService } from '@ngx-translate/core';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { CustomerStoreService } from '@app/ngrx-customer/services';

@Injectable({
  providedIn: 'root',
})
export class FoodCostCalculatorService {
  isLoading$: Observable<boolean>;
  graphData$ = new BehaviorSubject<any[]>([]);
  range$ = new BehaviorSubject<any>(undefined);
  public showDeleteFoodCostReportModal: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public showFoodCostReportDetailsModal: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public selectedFoodCostReport: BehaviorSubject<FoodCostReportState> =
    new BehaviorSubject(null);

  foodPurchaseData$ = new BehaviorSubject<any>(undefined);
  constructor(
    readonly inventoryService: InventoryService,
    readonly translateService: TranslateService,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    readonly platformService: PlatformService,
    public store: Store<State>,
    private userService: UserService,
    private datePipe: DatePipe,
    private customerStoreService: CustomerStoreService,
  ) {}

  getViewModel$ = (): Observable<FoodCostViewModel> => {
    // if not loaded then we are loading
    this.isLoading$ = this.inventoryService
      .inventoryStateIsLoaded$()
      .pipe(map(val => !val));
    return combineLatest([
      this.inventoryService.selectRadioBtnInventoriesForFoodCost$(),
      this.selectSortedFoodCostReportStates$(),
      this.userService.userPreferencesState$(),
      this.selectFoodCostGraphData$(),
      this.selectFoodCostSortOptions$(),
      this.selectFoodCostTileData$(),
    ]).pipe(
      map(
        ([
          inventories,
          foodCostReports,
          userPreferencesState,
          graphData,
          sortOptions,
          overviewData,
        ]) => {
          // TODO: Update user types library so we dont have to cast to any
          const userPrefState = userPreferencesState as any;
          const foodCostRange =
            userPrefState?.preferences?.preferences?.foodCostRange;
          const hasFoodCostRange = !!foodCostRange;
          const showFoodCostRange =
            (userPreferencesState?.loaded && foodCostReports?.length > 0) ||
            hasFoodCostRange;

          const viewModel: FoodCostViewModel = {
            inventories,
            foodCostReports: foodCostReports.slice(),
            trendsTileData: undefined,
            graphData,
            itemSize:
              this.platformService?.platformType ===
              this.platformService?.platformEnum.mobile
                ? 121
                : 72,
            foodCostRange,
            hasFoodCostRange,
            showFoodCostRange,
            sortOptions,
            overviewData,
          };
          this.graphData$.next(graphData);
          this.range$.next(foodCostRange);
          return viewModel;
        },
      ),
    );
  };

  selectFoodCostRangeLoadingState$ =
    (): Observable<FoodCostRangeLoadingState> => {
      return this.userService.userPreferencesState$().pipe(
        filter(
          (userPreferencesState: UserPreferencesState) =>
            userPreferencesState?.loaded,
        ),
        first(),
        switchMap(() =>
          this.userService.userPreferencesState$().pipe(
            map((userPreferencesState: UserPreferencesState) => {
              if (userPreferencesState?.loading) {
                return FoodCostRangeLoadingState.loading;
              } else {
                if (userPreferencesState?.error) {
                  return FoodCostRangeLoadingState.error;
                } else {
                  return FoodCostRangeLoadingState.success;
                }
              }
            }),
          ),
        ),
      );
    };

  selectFoodCostRangeOnceLoaded$ = (): Observable<any> => {
    return this.userService.userPreferencesState$().pipe(
      filter(
        (userPreferencesState: UserPreferencesState) =>
          userPreferencesState?.loaded,
      ),
      map((userPreferencesState: any) => {
        return userPreferencesState?.preferences?.preferences?.foodCostRange;
      }),
    );
  };

  loadFoodCostReports = () => {
    this.store.dispatch(FOOD_COST_ACTIONS.load());
  };

  resetFoodCostReportsState = () => {
    this.store.dispatch(FOOD_COST_ACTIONS.resetStore());
  };

  selectInventoriesForFoodCostModal$ = () => {
    return this.inventoryService.selectRadioBtnInventoriesForFoodCost$();
  };

  setupInventoriesForFoodCost = () => {
    this.store.dispatch(INVENTORY_ACTIONS.resetStore());
    this.store.dispatch(INVENTORY_ACTIONS.loadWithAllDepartments());
  };

  stripExtraDepartmentsOnLeave = async () => {
    let customer = await firstValueFrom(
      this.store.select(selectAppStateSelectedCustomer),
    );
    this.store.dispatch(
      INVENTORY_ACTIONS.stripAdditionalDepartments({
        departmentNumber: Number(customer.departmentNumber).valueOf(),
      }),
    );
  };

  setRange = async (start: number, end: number) => {
    const oldPreferences: Preferences = await firstValueFrom(
      this.userService.userPreferences$(),
    );
    const newPreferences = {
      ...oldPreferences,
      preferences: {
        ...oldPreferences?.preferences,
        // Have to do this since it is required in the object. TODO: Make this field an optional in the types
        recentlyViewed: oldPreferences?.preferences?.recentlyViewed ?? [],
        foodCostRange: {
          start,
          end,
        },
      },
    } as Preferences;
    this.store.dispatch(
      UserActions.updateUserPreferences({
        newPreferences,
        oldPreferences,
      }),
    );
  };

  createFoodCostReport(submitMap: Map<string, any>) {
    this.store.dispatch(
      FOOD_COST_ACTIONS.create({
        reportFields: transformFoodCostReport(submitMap),
      }),
    );
  }

  setSort(headerName: string) {
    this.store.dispatch(FOOD_COST_ACTIONS.setSort({ headerName }));
  }

  resetSort() {
    this.store.dispatch(FOOD_COST_ACTIONS.resetSort());
  }

  selectFoodCostReportStates$(): Observable<FoodCostReportState[]> {
    return combineLatest([
      this.store.select(
        foodCostReportAdapter.getSelectors(selectFoodCostState).selectAll,
      ),
      this.userService.userPreferencesFoodCostRange$(),
    ]).pipe(
      map(([foodCostReports, foodCostRange]) => {
        let foodCostReportStates: FoodCostReportState[] = [];
        for (const foodCostReport of foodCostReports) {
          let target: FoodCostTarget;
          if (!foodCostRange) {
            // target undefined: N/A state
          } else if (foodCostReport.foodCostPercentage > foodCostRange?.end) {
            target = FoodCostTarget.aboveTarget;
          } else if (foodCostReport.foodCostPercentage < foodCostRange?.start) {
            target = FoodCostTarget.belowTarget;
          } else {
            target = FoodCostTarget.onTrack;
          }

          const foodCostReportState = {
            ...foodCostReport,
            target,
          } as FoodCostReportState;
          foodCostReportStates.push(foodCostReportState);
        }
        return foodCostReportStates;
      }),
    );
  }

  selectFoodCostSortOptions$() {
    return this.store.select(selectFoodCostSortOptions());
  }

  selectFoodCostTileData$(): Observable<FoodCostOverviewData> {
    return combineLatest([
      this.selectFoodCostReportStates$(),
      this.userService.userPreferencesFoodCostRange$(),
    ]).pipe(
      map(([foodCostReports, foodCostRange]) => {
        let overviewData: FoodCostOverviewData;
        const hasFoodCostRange = !!foodCostRange;
        const sortedFoodCostReports = this.sortByCreationDate(
          foodCostReports,
          SortDirections.desc,
        );

        // We want the last two created reports to generate tile data
        if (sortedFoodCostReports?.length >= 2 && hasFoodCostRange) {
          const lastReport = sortedFoodCostReports[0];
          const secondToLastReport = sortedFoodCostReports[1];
          let targetState: FoodCostTarget;

          if (
            lastReport?.foodCostPercentage >= foodCostRange?.start &&
            lastReport?.foodCostPercentage <= foodCostRange?.end
          ) {
            targetState = FoodCostTarget.onTrack;
          } else if (lastReport?.foodCostPercentage >= foodCostRange?.end) {
            targetState = FoodCostTarget.aboveTarget;
          } else {
            targetState = FoodCostTarget.belowTarget;
          }
          overviewData = {
            foodCostRange,
            startValue: secondToLastReport.foodCostPercentage,
            startDate: this.datePipe.transform(
              secondToLastReport.endPeriod,
              'M/d/yyyy',
            ),
            endValue: lastReport.foodCostPercentage,
            endDate: this.datePipe.transform(lastReport.endPeriod, 'M/d/yyyy'),
            rangeAndDataReady: true,
            reportsReady: true,
            targetState,
          };
        } else {
          overviewData = {
            foodCostRange,
            startValue: 0,
            startDate: '',
            endValue: 0,
            endDate: '',
            rangeAndDataReady: false,
            reportsReady: sortedFoodCostReports?.length >= 2,
            targetState: FoodCostTarget.onTrack,
          };
        }
        return overviewData;
      }),
    );
  }

  selectFoodCostGraphData$() {
    return this.selectFoodCostReportStates$().pipe(
      map(foodCostReports => {
        let graphData = foodCostReports
          .filter(
            report =>
              report.foodCostPercentage <= 100 &&
              report.foodCostPercentage >= 0,
          )
          .map(report => {
            return {
              x: report.endPeriod,
              y: report.foodCostPercentage,
            };
          });

        graphData.sort((a, b) => {
          const date1 = new Date(a?.x);
          const date2 = new Date(b?.x);
          return date1.getTime() - date2.getTime();
        });

        if (graphData.length > 1) {
          const lastDate = new Date(graphData[graphData.length - 1]?.x);
          lastDate.setUTCFullYear(lastDate.getUTCFullYear() - 1);
          graphData = graphData.filter(data => {
            return isDateGreater(new Date(data.x), lastDate);
          });
        }

        return graphData;
      }),
    );
  }

  selectSortedFoodCostReportStates$(): Observable<FoodCostReportState[]> {
    return combineLatest([
      this.selectFoodCostReportStates$(),
      this.selectFoodCostSortOptions$(),
    ]).pipe(
      map(([foodCostReportStates, sortOptions]) => {
        if (
          this.platformService.platformType ===
          this.platformService.platformEnum.mobile
        ) {
          return this.sortByCreationDate(
            foodCostReportStates,
            SortDirections.desc,
          );
        }

        if (
          sortOptions?.selectedHeader === FoodCostSortEnum.dateCreatedHeaderName
        ) {
          return this.sortByCreationDate(
            foodCostReportStates,
            sortOptions.dateCreated.sortDirection,
          );
        } else if (
          sortOptions?.selectedHeader === FoodCostSortEnum.dateRangeHeaderName
        ) {
          return this.sortByDateRange(
            foodCostReportStates,
            sortOptions.dateRange.sortDirection,
          );
        } else {
          return this.sortByFoodCostPercentage(
            foodCostReportStates,
            sortOptions.foodCostPercentage.sortDirection,
          );
        }
      }),
    );
  }

  sortByCreationDate(
    foodCostReports: FoodCostReportState[],
    sortDirection: SortDirections,
  ): FoodCostReportState[] {
    return foodCostReports.sort((a, b) => {
      const date1 = new Date(a?.r4AddDtm);
      const date2 = new Date(b?.r4AddDtm);
      return sortDirection === SortDirections.desc
        ? date2.getTime() - date1.getTime()
        : date1.getTime() - date2.getTime();
    });
  }

  sortByDateRange(
    foodCostReports: FoodCostReportState[],
    sortDirection: SortDirections,
  ): FoodCostReportState[] {
    return foodCostReports.sort((a, b) => {
      const transformedEndDate1 = this.datePipe.transform(
        a?.endPeriod,
        'M/d/yyyy',
      );
      const transformedEndDate2 = this.datePipe.transform(
        b?.endPeriod,
        'M/d/yyyy',
      );
      const endDate1 = new Date(a?.endPeriod);
      const endDate2 = new Date(b?.endPeriod);
      // Secondary Sort
      if (transformedEndDate1 === transformedEndDate2) {
        const date1 = new Date(a?.r4AddDtm);
        const date2 = new Date(b?.r4AddDtm);
        return date2.getTime() - date1.getTime();
      }
      return sortDirection === SortDirections.desc
        ? endDate2.getTime() - endDate1.getTime()
        : endDate1.getTime() - endDate2.getTime();
    });
  }

  sortByFoodCostPercentage(
    foodCostReports: FoodCostReportState[],
    sortDirection: SortDirections,
  ): FoodCostReportState[] {
    return foodCostReports.sort((a, b) => {
      // Secondary Sort
      if (a?.foodCostPercentage === b?.foodCostPercentage) {
        const date1 = new Date(a?.r4AddDtm);
        const date2 = new Date(b?.r4AddDtm);
        return date2.getTime() - date1.getTime();
      }
      return sortDirection === SortDirections.desc
        ? a?.foodCostPercentage > b?.foodCostPercentage
          ? -1
          : 1
        : a?.foodCostPercentage > b?.foodCostPercentage
          ? 1
          : -1;
    });
  }

  async openCostReportCardEllipsis(data: CostReportCardEllipsisClick) {
    const buttons: any[] = [];
    buttons.push({
      text: this.translateService.instant('i18n.common.view'),
      handler: () => {
        if (this.platformService.platformType === PlatformEnum.desktop) {
          this.popoverController.dismiss();
        }
        this.setSelectedFoodCostReport(data.foodCostReport);
        this.showHideFoodCostReportDetailsModal(true);
        true;
      },
    });

    buttons.push({
      text: this.translateService.instant('i18n.inventory.delete'),
      handler: () => {
        if (this.platformService.platformType === PlatformEnum.desktop) {
          this.popoverController.dismiss();
        }
        this.setSelectedFoodCostReport(data.foodCostReport);
        this.showHideDeleteFoodCostReportModal(true);
      },
    });

    this.platformService.platformType === PlatformEnum.desktop
      ? this.openInventoryCardEllipsisDesktop(data.event, buttons)
      : this.openInventoryCardEllipsisTouch(buttons);
  }

  async openInventoryCardEllipsisDesktop(event: Event, buttons: any[]) {
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      event,
      componentProps: {
        buttons,
      },
      cssClass: 'ellipsis-options-popover-desktop',
      mode: 'ios',
      showBackdrop: false,
    });
    await popover.present();
  }

  async openInventoryCardEllipsisTouch(buttons: any[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });

    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  public showHideDeleteFoodCostReportModal(show: boolean) {
    this.showDeleteFoodCostReportModal.next(show);
  }

  public showHideFoodCostReportDetailsModal(show: boolean) {
    this.showFoodCostReportDetailsModal.next(show);
  }

  public setSelectedFoodCostReport(foodCostRepoort: FoodCostReportState) {
    this.selectedFoodCostReport.next(foodCostRepoort);
  }

  public openFoodCostReportDetailsModal(data: CostReportCardEllipsisClick) {
    this.setSelectedFoodCostReport(data.foodCostReport);
    this.showHideFoodCostReportDetailsModal(true);
  }

  async deleteFoodCostReport(foodCostReport: FoodCostReportState) {
    await firstValueFrom(
      combineLatest([
        this.store.select(selectUser),
        this.customerStoreService.loadSelectedCustomer$(),
        this.customerStoreService.loadSelectedCustomerDivision$(),
      ]).pipe(
        take(1),
        map(([userState, customer, division]) => {
          this.store.dispatch(
            FOOD_COST_ACTIONS.delete({
              customerNumber: customer.customerNumber,
              divisionNumber: division.divisionNumber,
              user: userState?.user,
              foodCostReport: foodCostReport,
            }),
          );
        }),
      ),
    );
  }

  recieveFoodPurchaseData = (data: any) => {
    this.foodPurchaseData$.next(data);
  };

  sendDataForFoodPurchase = (data: FoodCostTotalData) => {
    this.store.dispatch(FOOD_COST_ACTIONS.purchaseTotal(data));
  };

  trackFoodCostCalculatorPageLoaded() {
    this.store.dispatch(
      ANALYTICS_ACTIONS.trackFoodCostCalculatorPageLoaded({
        tracking: {
          analytics: {
            data: {
              capability: InventoryAnalyticsConstants.capability,
              event: InventoryAnalyticsConstants.pageLoaded,
              pageName: InventoryAnalyticsConstants.foodCostCalculatorPage,
            },
            transformFunc: pageLoadedTransformer,
          },
        },
      }),
    );
  }

  trackCreateNewReportModal() {
    this.store.dispatch(
      ANALYTICS_ACTIONS.trackCreateNewReportModal({
        tracking: {
          analytics: {
            data: {
              capability: InventoryAnalyticsConstants.listCapability,
              event: InventoryAnalyticsConstants.modalOpen,
              modalName: InventoryAnalyticsConstants.createNewFoodCostReport,
            },
            transformFunc: modalAnalyticsTransformer,
          },
        },
      }),
    );
  }
}
